import { Badge } from '@karnov/kui-react'
import { MarkdownToJSX } from 'markdown-to-jsx'
import { nanoid } from 'nanoid'
import { ComponentProps, isValidElement, ReactElement, ReactNode } from 'react'

import { DetailsAccordion } from '../components/DetailsAccordion'
import { InlineReference } from '../components/InlineReference'
import { Link } from '../components/Link'

import { RiskIndicator } from '../components/RiskIndicator'
import { MarkdownConfigurationInput } from '../configuration'

const reactNodeToText = (node: ReactNode): string => {
  const randomKey = nanoid()
  if (node === null || node === undefined || node === false) return randomKey
  if (typeof node === 'string' || typeof node === 'number') return String(node)

  if (Array.isArray(node)) {
    return node.map(reactNodeToText).join('')
  }

  if (isValidElement(node)) {
    const element = node as ReactElement
    // If this is a component, we only care about its children for text
    return reactNodeToText(element.props.children)
  }

  return randomKey
}

export const customComponents = ({ messageId }: MarkdownConfigurationInput) =>
  ({
    Badge: {
      component: ({ variant, children }: ComponentProps<typeof Badge>) => (
        <Badge variant={variant}>{children}</Badge>
      ),
    },
    InlineReference: {
      component: ({ referenceId, referenceNumber }: ComponentProps<typeof InlineReference>) => (
        <InlineReference
          messageId={messageId}
          referenceId={referenceId}
          referenceNumber={referenceNumber}
        />
      ),
    },
    RiskIndicator: {
      component: ({ variant, children }: ComponentProps<typeof RiskIndicator>) => (
        <RiskIndicator variant={variant}>{children}</RiskIndicator>
      ),
    },
    ThrowError: {
      component: () => {
        throw new Error('This is a simulated error from ThrowError component')
      },
    },
    a: {
      component: Link,
      props: {
        messageId,
      },
    },
    details: {
      component: ({ children }: ComponentProps<typeof Badge>) => {
        if (!Array.isArray(children)) {
          return null
        }

        const summary = children?.[0]
        const badge = children?.[1]
        const contentChildren = children.slice(2)
        const accordionId = `${messageId}_${reactNodeToText(summary)}`

        return (
          <DetailsAccordion badge={badge} id={accordionId} isLoading={!badge} messageId={messageId} summary={summary}>{contentChildren}</DetailsAccordion>
        )
      },
    },
  } satisfies MarkdownToJSX.Overrides)
